import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { SERVICE_TYPES, EQUIPMENTS, SCHEDULE_TIMES } from "@/lib/status";
import { Checkbox } from "@/components/ui/checkbox";
import { ImagePlus, X, Loader2, CalendarClock, Video, FileText, AlertTriangle, Film, Sparkles, MapPin, Ticket } from "lucide-react";
import { toast } from "sonner";
import AvailabilityPicker from "@/components/app/AvailabilityPicker";
import { isAddressComplete } from "@/components/app/AddressFields";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  service_type: "", equipment: "", model: "", defect_description: "",
  is_warranty: false, availability: "", address_same_as_registered: true,
  service_cep: "", service_address: "", service_responsible_name: "",
  service_responsible_phone: "", customer_name: "", customer_phone: "", customer_email: "", customer_document: "", customer_id: null, customer_has_address: false,
  customer_is_contract: false, equipment_id: null, service_location: "", register_equipment_in_field: false,
  scheduled_date: "", scheduled_time: "", observations: "", is_emergency: false, is_quote: false,
  availability_date: "", availability_period: "", availability_special: "",
};

export default function NewRequestDialog({ open, onOpenChange, onCreated, managerMode = false, editRequest = null, lockedEquipment = null, defaultEmergency = false, preset = null, initialContract = null }) {
  const { user } = useAuth();
  const regAddrOk = managerMode || isAddressComplete(user?.address);
  const isEdit = !!editRequest;
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState("");
  const [geo, setGeo] = useState(null); // { lat, lng }
  const [geoStatus, setGeoStatus] = useState(""); // "loading" | "ok" | "error"
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState("");
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custSearching, setCustSearching] = useState(false);
  const [contractEquipment, setContractEquipment] = useState([]);

  const loadContractEquipment = async (contractId) => {
    try {
      const { data } = await api.get(`/contracts/${contractId}/equipment`);
      setContractEquipment(data || []);
    } catch { setContractEquipment([]); }
  };

  const pickEquipment = (val) => {
    if (val === "not_registered") {
      setForm((f) => ({ ...f, equipment_id: null, register_equipment_in_field: true, service_location: "" }));
      return;
    }
    const eq = contractEquipment.find((e) => e.id === val);
    if (!eq) return;
    setForm((f) => ({
      ...f, equipment_id: eq.id, register_equipment_in_field: false,
      equipment: EQUIPMENTS.includes(eq.equipment_type) ? eq.equipment_type : (f.equipment || "Ar-condicionado"),
      model: eq.model || f.model,
      service_location: eq.location || f.service_location,
    }));
  };

  const searchCustomers = async (e) => {
    e?.preventDefault();
    setCustSearching(true);
    try {
      const { data } = await api.get("/customers", { params: { query: custQuery, include_contracts: true } });
      setCustResults(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha na busca");
    } finally {
      setCustSearching(false);
    }
  };

  const pickCustomer = (c) => {
    const a = c.address || {};
    const hasAddr = !!a.cep;
    const addressLine = [a.street, a.number, a.neighborhood, a.city, a.state].filter(Boolean).join(", ");
    const displayName = c.is_contract ? (c.company_name || c.name) : c.name;
    setForm((f) => ({
      ...f, customer_id: c.id, customer_has_address: hasAddr, customer_is_contract: !!c.is_contract,
      customer_name: displayName, customer_phone: c.phone || "", customer_email: c.email || "", customer_document: c.document || "",
      service_responsible_name: c.name, service_responsible_phone: c.phone || "",
      equipment_id: null, register_equipment_in_field: false, service_location: "",
      ...(hasAddr
        ? { address_same_as_registered: false, service_cep: a.cep || "", service_address: addressLine }
        : { address_same_as_registered: true, service_cep: "", service_address: "" }),
    }));
    if (c.is_contract) loadContractEquipment(c.id); else setContractEquipment([]);
    setCustResults([]);
    setCustQuery("");
    toast.success(`${c.is_contract ? "Cliente de contrato" : "Cliente"} ${displayName} vinculado.`);
  };

  const clearCustomer = () => {
    setContractEquipment([]);
    setForm((f) => ({
      ...f, customer_id: null, customer_has_address: false, customer_is_contract: false,
      customer_name: "", customer_phone: "", customer_email: "", customer_document: "",
      equipment_id: null, register_equipment_in_field: false, service_location: "",
      address_same_as_registered: true, service_cep: "", service_address: "",
    }));
  };

  useEffect(() => {
    if (!open) return;
    if (editRequest) {
      setForm({
        service_type: editRequest.service_type || "",
        equipment: editRequest.equipment || "",
        model: editRequest.model || "",
        defect_description: editRequest.defect_description || "",
        is_warranty: !!editRequest.is_warranty,
        availability: editRequest.availability || "",
        availability_date: editRequest.availability_date || "",
        availability_period: editRequest.availability_period || "",
        availability_special: editRequest.availability_special || "",
        address_same_as_registered: editRequest.address_same_as_registered !== false,
        service_cep: editRequest.service_cep || "",
        service_address: editRequest.service_address || "",
        service_responsible_name: editRequest.service_responsible_name || "",
        service_responsible_phone: editRequest.service_responsible_phone || "",
        customer_name: editRequest.customer_name || "",
        customer_phone: editRequest.customer_phone || "",
        customer_email: editRequest.customer_email || "",
        customer_document: editRequest.customer_document || "",
        customer_id: editRequest.customer_id || null,
        scheduled_date: editRequest.scheduled_date || "",
        scheduled_time: editRequest.scheduled_time || "",
        observations: editRequest.observations || "",
        is_emergency: !!editRequest.is_emergency,
      });
      setPhotos((editRequest.photos || []).map((p) => ({ path: p })));
      setVideos((editRequest.videos || []).map((p) => ({ path: p })));
      setInvoice(editRequest.invoice || null);
    } else {
      setForm({
        ...EMPTY,
        is_emergency: defaultEmergency || !!preset?.is_emergency,
        equipment: preset?.equipment || (lockedEquipment ? "Ar-condicionado" : ""),
        model: lockedEquipment?.model || "",
        service_type: preset?.service_type || "",
        is_quote: !!preset?.is_quote,
        address_same_as_registered: regAddrOk,
      });
      setPhotos([]);
      setVideos([]);
      setInvoice(null);
      setContractEquipment([]);
      if (initialContract) {
        pickCustomer({
          id: initialContract.id, is_contract: true,
          company_name: initialContract.company_name, name: initialContract.name,
          phone: initialContract.phone, email: initialContract.email,
          document: initialContract.document, address: initialContract.address,
        });
      }
    }
    setDiagnosis("");
    setGeo(null);
    setGeoStatus("");
    setSelectedCoupon("");
    setCustResults([]);
    setCustQuery("");
    // busca cupons de fidelidade ativos do cliente (só ao abrir novo chamado)
    if (!managerMode && !editRequest) {
      api.get("/loyalty/me")
        .then(({ data }) => setCoupons((data.coupons || []).filter((c) => c.status === "ativo")))
        .catch(() => setCoupons([]));
    } else {
      setCoupons([]);
    }
  }, [open, editRequest, lockedEquipment, defaultEmergency, managerMode, preset, initialContract]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const runDiagnose = async () => {
    if (!form.equipment) { toast.error("Selecione o equipamento primeiro."); return; }
    setDiagnosing(true);
    setDiagnosis("");
    try {
      const { data } = await api.post("/ai/diagnose", {
        equipment: form.equipment,
        service_type: form.service_type || null,
        model: form.model || null,
        defect_description: form.defect_description || null,
      });
      setDiagnosis(data.diagnosis || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível gerar o diagnóstico");
    } finally {
      setDiagnosing(false);
    }
  };

  const captureLocation = () => {
    if (!navigator.geolocation) { toast.error("Localização não suportada neste dispositivo."); return; }
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus("ok");
        toast.success("Localização capturada — vamos direcionar ao franqueado mais próximo.");
      },
      () => {
        setGeoStatus("error");
        toast.error("Não conseguimos acessar sua localização. Verifique a permissão do navegador.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (photos.length + files.length > 3) { toast.error("Máximo de 3 fotos."); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { path: data.path }]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar imagem");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const handleVideos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (videos.length + files.length > 2) { toast.error("Máximo de 2 vídeos."); return; }
    setMediaUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setVideos((p) => [...p, { path: data.path }]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar vídeo");
    } finally {
      setMediaUploading(false);
    }
  };

  const handleInvoice = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setMediaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoice(data.path);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar nota fiscal");
    } finally {
      setMediaUploading(false);
    }
  };

  const lookupCep = async (cep) => {
    const digits = (cep || "").replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) { toast.error("CEP não encontrado."); return; }
      set("service_address", [data.logradouro, data.bairro, `${data.localidade}-${data.uf}`].filter(Boolean).join(", "));
    } catch (e) { console.error("CEP lookup failed", e); } finally { setCepLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type || !form.equipment || !form.availability) {
      toast.error("Preencha tipo de atendimento, equipamento e disponibilidade."); return;
    }
    if (form.service_type === "Conserto" && !form.defect_description) {
      toast.error("Descreva o defeito do equipamento."); return;
    }
    if (!managerMode && form.address_same_as_registered && !regAddrOk) {
      toast.error("Seu cadastro não tem endereço completo. Desative a opção e informe o endereço de atendimento."); return;
    }
    if (!form.address_same_as_registered && !form.service_address) {
      toast.error("Informe o endereço de atendimento."); return;
    }
    if (!managerMode && !form.address_same_as_registered && (form.service_cep || "").replace(/\D/g, "").length !== 8) {
      toast.error("Informe o CEP do endereço de atendimento."); return;
    }
    if (managerMode && (!form.customer_name || !form.customer_phone)) {
      toast.error("Informe o nome e o telefone do cliente."); return;
    }
    setLoading(true);
    try {
      const payload = { ...form, photos: photos.map((p) => p.path), videos: videos.map((v) => v.path), invoice: invoice || null };
      if (geo) { payload.service_lat = geo.lat; payload.service_lng = geo.lng; }
      if (!isEdit && selectedCoupon) payload.coupon_code = selectedCoupon;
      if (!isEdit && form.is_quote) payload.is_quote = true;
      if (lockedEquipment) payload.equipment_id = lockedEquipment.id;
      if (isEdit) {
        await api.patch(`/requests/${editRequest.id}`, payload);
        toast.success("Chamado atualizado!");
      } else {
        await api.post("/requests", payload);
        toast.success("Chamado aberto com sucesso!");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar chamado" : "Abrir chamado"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Corrija os dados do atendimento." : "Preencha os dados do atendimento."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {lockedEquipment && (
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm" data-testid="locked-equipment-banner">
              <p className="font-semibold text-[#0a2a6c]">Equipamento do contrato</p>
              <p className="text-slate-600">
                {lockedEquipment.equipment_type}{lockedEquipment.brand ? ` · ${lockedEquipment.brand}` : ""}{lockedEquipment.model ? ` ${lockedEquipment.model}` : ""}
                {lockedEquipment.location ? ` — ${lockedEquipment.location}` : ""}
              </p>
            </div>
          )}
          {managerMode && (
            <div className="space-y-3 rounded-lg bg-blue-50 p-3" data-testid="manager-customer-block">
              <p className="text-xs font-semibold text-blue-700">Dados do cliente</p>

              {form.customer_id ? (
                <div className="flex items-center justify-between rounded-md bg-white border border-blue-200 px-3 py-2" data-testid="manager-linked-customer">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{form.customer_name}</p>
                    <p className="text-xs text-slate-500 truncate">{form.customer_has_address ? "Cliente cadastrado · endereço preenchido automaticamente" : "Cliente cadastrado · informe o endereço do atendimento abaixo"}</p>
                  </div>
                  <Button type="button" size="sm" variant="ghost" className="text-red-500 h-8" onClick={clearCustomer} data-testid="manager-unlink-customer">Trocar</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input value={custQuery} onChange={(e) => setCustQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchCustomers(e); } }}
                      placeholder="Buscar cliente ou contrato (nome, empresa, CPF/CNPJ)..." data-testid="manager-customer-search-input" />
                    <Button type="button" variant="outline" onClick={searchCustomers} disabled={custSearching} data-testid="manager-customer-search-button">
                      {custSearching ? "..." : "Buscar"}
                    </Button>
                  </div>
                  {custResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-blue-200 bg-white p-1" data-testid="manager-customer-results">
                      {custResults.map((c) => (
                        <button key={c.id} type="button" onClick={() => pickCustomer(c)}
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-blue-50 text-sm" data-testid={`manager-customer-pick-${c.id}`}>
                          <span className="font-medium text-slate-800 flex items-center gap-1.5">
                            {c.is_contract ? (c.company_name || c.name) : c.name}
                            {c.is_contract && <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-100 rounded px-1.5 py-0.5">Contrato</span>}
                          </span>
                          <span className="text-xs text-slate-500 block truncate">
                            {c.is_contract && c.name ? `${c.name} · ` : ""}{c.email} · {c.phone || "-"}{c.document ? ` · ${c.document}` : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Nome do cliente *</Label>
                <Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)}
                  disabled={!!form.customer_id} placeholder="Nome do cliente" data-testid="customer-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Telefone *</Label>
                  <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)}
                    disabled={!!form.customer_id} placeholder="(21) 99999-9999" data-testid="customer-phone-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>E-mail</Label>
                  <Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)}
                    disabled={!!form.customer_id} placeholder="opcional" data-testid="customer-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>CPF / CNPJ</Label>
                <Input value={form.customer_document} onChange={(e) => set("customer_document", e.target.value)}
                  disabled={!!form.customer_id} placeholder="000.000.000-00 (aparece no orçamento/OS)" data-testid="customer-document-input" />
              </div>
            </div>
          )}

          {form.customer_is_contract && (
            <div className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3" data-testid="contract-equipment-block">
              <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Equipamento e local do atendimento</p>
              <div className="space-y-1.5">
                <Label>Equipamento do local</Label>
                <Select value={form.equipment_id || (form.register_equipment_in_field ? "not_registered" : "")} onValueChange={pickEquipment}>
                  <SelectTrigger data-testid="contract-equipment-select"><SelectValue placeholder="Selecione o aparelho cadastrado" /></SelectTrigger>
                  <SelectContent>
                    {contractEquipment.map((eq) => (
                      <SelectItem key={eq.id} value={eq.id} data-testid={`contract-equipment-${eq.id}`}>
                        {[eq.equipment_type, eq.brand, eq.model].filter(Boolean).join(" ")}{eq.location ? ` — ${eq.location}` : ""}
                      </SelectItem>
                    ))}
                    <SelectItem value="not_registered" data-testid="contract-equipment-none">Equipamento não cadastrado</SelectItem>
                  </SelectContent>
                </Select>
                {contractEquipment.length === 0 && <p className="text-xs text-slate-500">Nenhum equipamento cadastrado para este cliente ainda.</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Local / Sala</Label>
                <Input value={form.service_location} onChange={(e) => set("service_location", e.target.value)}
                  placeholder="Ex: Sala 203, Recepção, Sala de reunião..." data-testid="service-location-input" />
              </div>
              {form.register_equipment_in_field && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="field-register-note">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  O técnico vai cadastrar o equipamento no local durante o atendimento.
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Tipo de atendimento *</Label>
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
              <SelectTrigger data-testid="service-type-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`service-type-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <Label className="text-sm">Atendimento em garantia?</Label>
              <p className="text-xs text-slate-400">Ative se o equipamento está na garantia</p>
            </div>
            <Switch checked={form.is_warranty} onCheckedChange={(v) => set("is_warranty", v)} data-testid="warranty-switch" />
          </div>

          <div className="space-y-1.5">
            <Label>Equipamento *</Label>
            <Select value={form.equipment} onValueChange={(v) => set("equipment", v)}>
              <SelectTrigger data-testid="equipment-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {EQUIPMENTS.map((t) => <SelectItem key={t} value={t} data-testid={`equipment-${t}`}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="model">Modelo do equipamento</Label>
            <Input id="model" value={form.model} onChange={(e) => set("model", e.target.value)}
              placeholder="Ex: Brastemp BRM44" data-testid="model-input" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="defect">Defeito apresentado {form.service_type === "Conserto" && "*"}</Label>
            <Textarea id="defect" value={form.defect_description} onChange={(e) => set("defect_description", e.target.value)}
              placeholder="Descreva o problema do equipamento" rows={3} data-testid="defect-input" />
          </div>

          {!isEdit && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 space-y-2" data-testid="ai-diagnose-block">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-semibold text-indigo-800">Pré-diagnóstico inteligente</span>
                </div>
                <Button type="button" size="sm" variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  onClick={runDiagnose} disabled={diagnosing || !form.equipment}
                  data-testid="ai-diagnose-button">
                  {diagnosing ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Analisando...</> : "Gerar diagnóstico"}
                </Button>
              </div>
              <p className="text-xs text-indigo-700/70">
                Nossa IA sugere a causa provável e a urgência com base no equipamento e no problema informado.
              </p>
              {diagnosis && (
                <div className="rounded-md bg-white border border-indigo-100 p-3 text-sm text-slate-700 whitespace-pre-line" data-testid="ai-diagnose-result">
                  {diagnosis}
                  <p className="mt-2 text-[11px] text-slate-400">Sugestão gerada por IA — o diagnóstico final é feito pelo técnico na visita.</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <AvailabilityPicker allowPast={isEdit}
              value={{ availability_date: form.availability_date, availability_period: form.availability_period, availability_special: form.availability_special }}
              onChange={(v) => setForm((f) => ({ ...f, ...v }))} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="observations">Observações do atendimento</Label>
            <Textarea id="observations" value={form.observations} onChange={(e) => set("observations", e.target.value)}
              placeholder="Detalhes da instalação, conserto ou visita técnica" rows={3} data-testid="observations-input" />
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#1e7be0]" /> Agendamento (opcional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="sched-date" className="text-xs">Data</Label>
                <Input id="sched-date" type="date" value={form.scheduled_date}
                  onChange={(e) => set("scheduled_date", e.target.value)} data-testid="schedule-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Horário (08h–18h)</Label>
                <Select value={form.scheduled_time || "none"} onValueChange={(v) => set("scheduled_time", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="schedule-time-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="schedule-time-none">Sem horário</SelectItem>
                    {SCHEDULE_TIMES.map((t) => <SelectItem key={t} value={t} data-testid={`schedule-time-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Fotos do equipamento (até 3)</Label>
            <div className="flex gap-2 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative h-16 w-16">
                  <img src={fileUrl(p.path)} alt="foto" className="h-16 w-16 object-cover rounded-md border border-slate-200" />
                  <button type="button" onClick={() => removePhoto(i)}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-photo-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < 3 && (
                <label className="h-16 w-16 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-blue-400" data-testid="photo-upload-label">
                  {uploading ? <Loader2 className="h-5 w-5 text-slate-400 animate-spin" /> : <ImagePlus className="h-5 w-5 text-slate-400" />}
                  <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={handleFiles} data-testid="photo-input" />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Vídeos (até 2 · máx. 50MB)</Label>
            <div className="flex gap-2 flex-wrap items-center">
              {videos.map((v, i) => (
                <div key={i} className="relative h-16 w-16 rounded-md border border-slate-200 bg-slate-900 flex items-center justify-center">
                  <Film className="h-6 w-6 text-white/80" />
                  <button type="button" onClick={() => setVideos((arr) => arr.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-video-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {videos.length < 2 && (
                <label className="h-16 w-16 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-blue-400" data-testid="video-upload-label">
                  {mediaUploading ? <Loader2 className="h-5 w-5 text-slate-400 animate-spin" /> : <Video className="h-5 w-5 text-slate-400" />}
                  <input type="file" accept="video/mp4,video/quicktime,video/webm" multiple className="hidden" onChange={handleVideos} data-testid="video-input" />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Nota fiscal (PDF ou imagem)</Label>
            {invoice ? (
              <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2" data-testid="invoice-preview">
                <FileText className="h-4 w-4 text-[#1e7be0]" />
                <a href={fileUrl(invoice)} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline flex-1 truncate">Ver nota fiscal enviada</a>
                <button type="button" onClick={() => setInvoice(null)} className="text-red-500" data-testid="invoice-remove"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 rounded-md border-2 border-dashed border-slate-300 px-3 py-2 cursor-pointer hover:border-blue-400 text-slate-500 text-sm" data-testid="invoice-upload-label">
                {mediaUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Enviar nota fiscal
                <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={handleInvoice} data-testid="invoice-input" />
              </label>
            )}
          </div>

          <label className="flex items-center gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3 cursor-pointer" data-testid="emergency-block">
            <Checkbox checked={form.is_emergency} onCheckedChange={(v) => set("is_emergency", !!v)} data-testid="emergency-checkbox" />
            <span className="flex items-center gap-1.5 text-sm font-medium text-red-700">
              <AlertTriangle className="h-4 w-4" /> Atendimento emergencial (urgente)
            </span>
          </label>

          {!isEdit && coupons.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="coupon-apply-block">
              <div className="flex items-center gap-1.5">
                <Ticket className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">Usar cupom de fidelidade</span>
              </div>
              <p className="text-xs text-amber-700/70">O desconto será aplicado no valor do serviço desta OS.</p>
              <Select value={selectedCoupon || "none"} onValueChange={(v) => setSelectedCoupon(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="coupon-select"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="coupon-none">Não usar cupom</SelectItem>
                  {coupons.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`coupon-option-${c.code}`}>
                      {c.code} — R$ {Number(c.discount).toFixed(2).replace(".", ",")} de desconto
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!isEdit && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2" data-testid="geolocation-block">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-800">Usar minha localização</span>
                </div>
                <Button type="button" size="sm" variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  onClick={captureLocation} disabled={geoStatus === "loading"}
                  data-testid="geolocation-button">
                  {geoStatus === "loading" ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Localizando...</> : (geoStatus === "ok" ? "Atualizar" : "Compartilhar")}
                </Button>
              </div>
              <p className="text-xs text-emerald-700/70">
                Direciona o chamado ao franqueado mais próximo, mesmo sem o CEP.
              </p>
              {geoStatus === "ok" && geo && (
                <p className="text-xs font-medium text-emerald-700" data-testid="geolocation-status">
                  Localização capturada ({geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}).
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <Label className="text-sm">Atender no endereço do cadastro?</Label>
              <p className="text-xs text-slate-400">Desative para informar outro endereço</p>
              {!regAddrOk && (
                <p className="text-xs text-amber-600 mt-1" data-testid="no-registered-address-hint">Seu cadastro não tem endereço completo — informe o endereço de atendimento.</p>
              )}
            </div>
            <Switch checked={form.address_same_as_registered}
              onCheckedChange={(v) => set("address_same_as_registered", v)} data-testid="address-same-switch" />
          </div>

          {!form.address_same_as_registered && (
            <div className="space-y-3 rounded-lg bg-slate-50 p-3" data-testid="alt-address-block">
              <div className="space-y-1.5">
                <Label htmlFor="cep">CEP{!managerMode && " *"}</Label>
                <Input id="cep" value={form.service_cep} onChange={(e) => set("service_cep", e.target.value)}
                  onBlur={(e) => lookupCep(e.target.value)} placeholder="00000-000" data-testid="cep-input" />
                {cepLoading && <p className="text-xs text-slate-400">Buscando endereço...</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="addr">Endereço completo *</Label>
                <Input id="addr" value={form.service_address} onChange={(e) => set("service_address", e.target.value)}
                  placeholder="Rua, número, bairro, cidade" data-testid="address-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="resp">Responsável que vai receber a equipe</Label>
                <Input id="resp" value={form.service_responsible_name} onChange={(e) => set("service_responsible_name", e.target.value)}
                  placeholder="Nome de quem vai receber" data-testid="responsible-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="resp-phone">Telefone do responsável</Label>
                <Input id="resp-phone" value={form.service_responsible_phone} onChange={(e) => set("service_responsible_phone", e.target.value)}
                  placeholder="(21) 99999-9999" data-testid="responsible-phone-input" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="submit-request-button">
              {loading ? "Salvando..." : isEdit ? "Salvar alterações" : "Abrir chamado"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
